import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAIMDWizard, AIMDWizardReturn } from '../hooks/useAIMDWizard';
import {
  AIMD_STEPS,
  ContentData,
  GenerationPhase,
  GenerationProgress,
  GenerationStatus,
  ModuleStructure,
  ProgramStructure,
  AudiencePayloadItem
} from '../types';
import * as aimdApi from '../api';
import { collectChangedLessonKeys } from '../api';
import { lessonTagFields } from '../lessonTags';
import { preserveVideos } from '../utils/lessonMedia';
import { API, CourseDraftSaveReason } from '../../../../../api';
import { getProgramContext } from '../../../../../hooks/useProgramContext';
import useEditingFrozen from '../../../../../hooks/useEditingFrozen';
import { useAppDispatch } from '../../../../../redux/hooks';
import { fetchGroupDetails } from '../../../../../redux/slices/group-details/groupDetails';

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

/** Outcome of a Content step save (PATCH program-generation/{id}/content). */
export interface ContentSaveResult {
  ok: boolean;
  /** Lessons the BE wrote (0 when nothing was dirty). */
  saved: number;
  /** Course versioning: an autosave version was written (informational). */
  versionCreated: boolean;
  /** HTTP status of a failed save, when known (423 = editing frozen, already toasted by the interceptor). */
  status?: number;
  message?: string;
}

interface AIMDContextValue extends AIMDWizardReturn {
  generationPhase: GenerationPhase;
  isGenerating: boolean;
  pollingActive: boolean;
  generationProgress: GenerationProgress | null;
  generationError: string | null;

  handleGeneratePreview: () => Promise<void>;
  handleImprovePreview: (feedback: string) => Promise<void>;
  handleGenerateStructure: () => Promise<void>;
  handleImproveModule: (moduleIndex: number, feedback: string) => Promise<void>;
  handleImproveProgram: (feedback: string) => Promise<void>;
  /**
   * Saves the structure (`modules` when given: an edit made in the same tick,
   * before the state has it), and takes back what the backend keeps on each
   * lesson: its tags and whether it has a Scored Roleplay.
   */
  handleSaveStructure: (modules?: ModuleStructure[]) => Promise<void>;
  /** When the structure was last saved (0 before any): an edit that only lived in the browser is now on the server. */
  structureSavedAt: number;
  /**
   * Persists the lessons the admin edited since the last successful save
   * (body, quiz, materials, title) on the generation artifact. Dirty keys
   * survive a failed save so the next attempt retries them. `reason` is the
   * versioning autosave hint ('hidden' / 'close' force a version).
   */
  handleSaveContent: (reason?: CourseDraftSaveReason) => Promise<ContentSaveResult>;
  /** Forget every pending lesson edit (after the artifact was reloaded from the server). */
  markAllLessonsClean: () => void;
  /**
   * Reload the structure and content from GET program-generation/{id}/status
   * (after a version restore), the way the resume hydration does: structure
   * = the server's modules, content = the server payload when any lesson has
   * a body, else null. Marks every lesson clean. Never triggers generation
   * by itself; the caller decides where to send the admin when the reloaded
   * artifact has no content.
   */
  reloadGenerationFromServer: () => Promise<{ hasContent: boolean }>;
  handleGenerateContent: () => Promise<void>;
  handleRegenerateLesson: (moduleIndex: number, lessonIndex: number, feedback: string) => Promise<void>;
  handleRegenerateModule: (moduleIndex: number, feedback: string) => Promise<void>;
  handleRegenerateProgram: (feedback: string) => Promise<void>;
  handlePublish: () => Promise<void>;
  startContentPolling: () => void;
  stopContentPolling: () => void;
  // True when the wizard runs inside the Add Program modal (vs the standalone
  // page) — steps use this to hide page-only chrome (e.g. the sidebar name box).
  embedded: boolean;
}

const AIMDContext = createContext<AIMDContextValue | null>(null);

// ---------------------------------------------------------------------------
// Helper: build common payload fields from wizard state
// ---------------------------------------------------------------------------

function getCommonPayload(wizard: AIMDWizardReturn) {
  const { state } = wizard;
  const learningObjectives = state.outcomes.outcomes
    .map((o) => o.value)
    .filter((v) => v.trim().length > 0);

  const allDocs = [...state.documents.uploadedDocuments, ...state.documents.previousDocuments];
  const orgDocsIds = allDocs
    .filter((d) => d.selected && d.processingStatus === 'indexed')
    .map((d) => d.id);

  const audience: AudiencePayloadItem[] | undefined = undefined;
  const tone = state.requirements.tone.length > 0 ? state.requirements.tone : undefined;

  return {
    generalPurpose: state.requirements.programDescription,
    learningObjectives,
    groupId: wizard.groupId,
    orgDocsIds,
    audience,
    tone,
    programName: state.requirements.programName,
    programPreview: state.preview.generatedPreview || undefined
  };
}

/**
 * Why a generation call failed, in words for the admin: the backend's message
 * when it gave one (a refused structure names the capability left with no
 * lesson), else the error's own.
 */
const failureMessage = (err: any, fallback: string): string => err?.response?.data?.message || err?.message || fallback;

/** A lesson as the generation endpoints take it: its title, and its tags in the Capability build flow. */
const lessonForGeneration = (l: { title: string; capabilityId?: string | null; outcomes?: string[] }) => ({
  title: l.title,
  ...lessonTagFields(l)
});

// ---------------------------------------------------------------------------
// Dirty lessons (Content step)
// ---------------------------------------------------------------------------
//
// Every functional `updateContent` call is diffed lesson by lesson (by
// position, which is how the artifact addresses them) and the lessons that
// changed are remembered here until a content save carries them. The
// polling patch (`updateContent({ generatedContent })`) is not an edit and is
// never tracked. The value is a sequence number that orders edits against an
// in-flight save, so an edit made while a request is out is kept for the
// next one.
type DirtyLessons = Map<string, number>;

const EMPTY_SAVE: ContentSaveResult = { ok: true, saved: 0, versionCreated: false };

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export const AIMDProvider: React.FC<{ children: React.ReactNode; embedded?: boolean }> = ({
  children,
  embedded = false
}) => {
  const wizard = useAIMDWizard();
  const dispatch = useAppDispatch();
  // Relaunch editing freeze (B4-2): the background content flushes stay quiet
  // while the org is frozen (the BE answers 423 and the interceptor toasts).
  const editingFrozen = useEditingFrozen();
  const editingFrozenRef = useRef(editingFrozen);
  editingFrozenRef.current = editingFrozen;

  const [generationPhase, setGenerationPhase] = useState<GenerationPhase>('idle');
  const [pollingActive, setPollingActive] = useState(false);
  // Extra polling budget after content flips to 'completed' while the
  // per-lesson Elliott sections are still being generated on the BE
  // (fire-and-forget cascade). 36 ticks ~= 3 minutes at the 5s interval.
  const elliottWaitTicksRef = useRef(0);
  const [generationProgress, setGenerationProgress] = useState<GenerationProgress | null>(null);
  const [generationError, setGenerationError] = useState<string | null>(null);

  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isGenerating = generationPhase !== 'idle';

  // Deduplicate concurrent calls: store the in-flight promise so a second
  // call awaits the same request instead of firing a new one.
  const wizardStateRef = useRef(wizard.state);
  wizardStateRef.current = wizard.state;

  const generationIdRef = useRef<string | null>(wizard.state.generationId);
  generationIdRef.current = wizard.state.generationId;
  const creationPromiseRef = useRef<Promise<string> | null>(null);

  // ----- Dirty lessons + content save -----

  const dirtyLessonsRef = useRef<DirtyLessons>(new Map());
  const dirtySeqRef = useRef(0);
  const contentSaveInflightRef = useRef<Promise<ContentSaveResult> | null>(null);

  const markLessonsDirty = useCallback((keys: string[]) => {
    if (!keys.length) return;
    const dirty = dirtyLessonsRef.current;
    keys.forEach((key) => dirty.set(key, ++dirtySeqRef.current));
  }, []);

  const markAllLessonsClean = useCallback(() => {
    dirtyLessonsRef.current.clear();
  }, []);

  // Functional updaters are the admin's edits (and the AI rewrites merged
  // into a lesson); diff them so the save knows what to send. The patch form
  // is the status polling and is passed through untouched.
  const { updateContent: rawUpdateContent, updateStructure: rawUpdateStructure } = wizard;
  const updateContent = useCallback<AIMDWizardReturn['updateContent']>(
    (patchOrUpdater) => {
      if (typeof patchOrUpdater !== 'function') {
        rawUpdateContent(patchOrUpdater);
        return;
      }
      rawUpdateContent((prev: ContentData): ContentData => {
        const next = patchOrUpdater(prev);
        if (next !== prev) {
          markLessonsDirty(
            collectChangedLessonKeys(
              prev.generatedContent as GenerationStatus | null,
              next.generatedContent as GenerationStatus | null
            )
          );
        }
        return next;
      });
    },
    [rawUpdateContent, markLessonsDirty]
  );

  const handleSaveContent = useCallback(
    async (reason?: CourseDraftSaveReason): Promise<ContentSaveResult> => {
      // One request at a time: a save fired while another is out waits for it
      // (the edits it did not carry are still dirty and go with this one).
      if (contentSaveInflightRef.current) {
        await contentSaveInflightRef.current.catch(() => undefined);
      }
      const genId = generationIdRef.current;
      const dirty = dirtyLessonsRef.current;
      if (!genId || dirty.size === 0) return EMPTY_SAVE;

      const content = wizardStateRef.current.content.generatedContent as GenerationStatus | null;
      const sent = new Map(dirty);
      const modules = aimdApi.buildContentPatch(content?.modules, sent.keys());
      if (!modules.length) {
        // Every dirty key points past the current lessons: nothing to write.
        sent.forEach((_, key) => dirty.delete(key));
        return EMPTY_SAVE;
      }

      const run = (async (): Promise<ContentSaveResult> => {
        try {
          const res = await aimdApi.saveProgramGenerationContent(genId, modules, reason);
          // Clear only what this request carried; a lesson edited meanwhile
          // has a newer seq and stays dirty for the next save.
          sent.forEach((seq, key) => {
            const cur = dirty.get(key);
            if (cur !== undefined && cur <= seq) dirty.delete(key);
          });
          return { ok: true, saved: res.lessonsUpdated, versionCreated: res.versionCreated };
        } catch (err: any) {
          console.error('[AIMDv2] saveContent failed', err);
          return {
            ok: false,
            saved: 0,
            versionCreated: false,
            status: err?.response?.status,
            message: err?.response?.data?.message
          };
        }
      })();
      contentSaveInflightRef.current = run;
      try {
        return await run;
      } finally {
        if (contentSaveInflightRef.current === run) contentSaveInflightRef.current = null;
      }
    },
    []
  );
  const handleSaveContentRef = useRef(handleSaveContent);
  handleSaveContentRef.current = handleSaveContent;

  const reloadGenerationFromServer = useCallback(async (): Promise<{ hasContent: boolean }> => {
    const genId = generationIdRef.current;
    if (!genId) return { hasContent: false };
    const server: GenerationStatus = await aimdApi.pollGenerationStatus(genId);
    const modules = Array.isArray(server?.modules) ? server.modules : [];
    const hasContent = modules.some((m: any) => Array.isArray(m?.lessons) && m.lessons.some((l: any) => l?.content));
    if (modules.length) {
      rawUpdateStructure({ generatedStructure: { modules } as unknown as ProgramStructure });
    }
    // Raw updater on purpose: a reload is not an edit (nothing becomes dirty)
    // and the patch form would keep locally-completed lessons, so replace.
    rawUpdateContent((prev: ContentData): ContentData => ({
      ...prev,
      generatedContent: hasContent ? server : null
    }));
    dirtyLessonsRef.current.clear();
    setGenerationProgress(server?.progress ?? null);
    return { hasContent };
  }, [rawUpdateStructure, rawUpdateContent]);

  // Tab hidden → a normal save with reason 'hidden' (forces a version when
  // versioning is on). Tab closed / reloaded → keepalive fetch with reason
  // 'close' (an XHR would be dropped; bodies over ~64 KB are refused, so a
  // very large flush can be lost — same trade-off as the Designer's draft).
  // Leaving the wizard inside the app flushes like a hidden tab.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden' || editingFrozenRef.current) return;
      handleSaveContentRef.current('hidden');
    };
    const onPageHide = () => {
      if (editingFrozenRef.current || contentSaveInflightRef.current) return;
      const genId = generationIdRef.current;
      const dirty = dirtyLessonsRef.current;
      if (!genId || dirty.size === 0) return;
      const content = wizardStateRef.current.content.generatedContent as GenerationStatus | null;
      const modules = aimdApi.buildContentPatch(content?.modules, dirty.keys());
      if (modules.length) aimdApi.saveProgramGenerationContentKeepalive(genId, modules);
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      if (dirtyLessonsRef.current.size && !editingFrozenRef.current) handleSaveContentRef.current('hidden');
    };
  }, []);

  const ensureGenerationId = useCallback(async (): Promise<string> => {
    if (generationIdRef.current) return generationIdRef.current;
    if (creationPromiseRef.current) return creationPromiseRef.current;

    // If the wizard already has a groupId (set via the LL Continue
    // hydration in AIMDv2Page), tell the BE to attach the new
    // generation to that existing program instead of spawning a fresh
    // "AIMD-{ts}" group. Otherwise the user would end up with a
    // duplicate program at publish time.
    const reuseGroupId = wizard.groupId || undefined;
    const promise = aimdApi
      .createProgramGeneration(reuseGroupId)
      .then(async ({ generationId, groupId }) => {
        generationIdRef.current = generationId;
        wizard.setGenerationId(generationId);
        wizard.setGroupId(groupId);

        // Rename the group to the user's chosen program name. Safe even
        // on the existing-group path — the BE rename is idempotent.
        const programName = wizardStateRef.current.requirements.programName;
        if (programName) {
          aimdApi.renameProgramGroup(generationId, programName).catch((err) =>
            console.error('[AIMDv2] Failed to rename group', err)
          );
        }

        creationPromiseRef.current = null;
        return generationId;
      });
    creationPromiseRef.current = promise;
    return promise;
  }, [wizard]);

  // ----- Preview -----

  const handleGeneratePreview = useCallback(async () => {
    try {
      setGenerationPhase('preview');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      const payload = getCommonPayload(wizard);
      const result = await aimdApi.generatePreview(genId, {
        generalPurpose: payload.generalPurpose,
        learningObjectives: payload.learningObjectives,
        programName: payload.programName
      });
      wizard.updatePreview({ generatedPreview: result.preview });
    } catch (err: any) {
      setGenerationError(err?.message || 'The preview could not be built. Try again.');
      console.error('[AIMDv2] generatePreview failed', err);
    } finally {
      setGenerationPhase('idle');
    }
  }, [wizard, ensureGenerationId]);

  const handleImprovePreview = useCallback(async (feedback: string) => {
    try {
      setGenerationPhase('preview');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      const payload = getCommonPayload(wizard);
      const result = await aimdApi.improvePreview(genId, {
        generalPurpose: payload.generalPurpose,
        learningObjectives: payload.learningObjectives,
        currentPreview: wizard.state.preview.generatedPreview || '',
        feedback,
        programName: payload.programName
      });
      wizard.updatePreview({ generatedPreview: result.preview });
    } catch (err: any) {
        setGenerationError(err?.message || 'Elliott could not improve the preview. Try again.');
      console.error('[AIMDv2] improvePreview failed', err);
    } finally {
      setGenerationPhase('idle');
    }
  }, [wizard, ensureGenerationId]);

  // ----- Structure -----

  const handleGenerateStructure = useCallback(async () => {
    try {
      setGenerationPhase('structure');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      const payload = getCommonPayload(wizard);
      const result = await aimdApi.generateStructure(genId, {
        generalPurpose: payload.generalPurpose,
        learningObjectives: payload.learningObjectives,
        groupId: payload.groupId,
        orgDocsIds: payload.orgDocsIds,
        programPreview: payload.programPreview
      });
      wizard.updateStructure({ generatedStructure: result as ProgramStructure });
    } catch (err: any) {
      setGenerationError(failureMessage(err, 'The structure could not be built. Try again.'));
      console.error('[AIMDv2] generateStructure failed', err);
    } finally {
      setGenerationPhase('idle');
    }
  }, [wizard, ensureGenerationId]);

  const handleImproveModule = useCallback(async (moduleIndex: number, feedback: string) => {
    try {
      setGenerationPhase('structure');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      const payload = getCommonPayload(wizard);
      const result = await aimdApi.improveModule(genId, {
        generalPurpose: payload.generalPurpose,
        learningObjectives: payload.learningObjectives,
        moduleIndex,
        feedback,
        programPreview: payload.programPreview
      });
      const current = wizard.state.structure.generatedStructure as ProgramStructure | null;
      if (result.modules) {
        // The Capability build flow: the whole structure, its Roleplays placed again across modules.
        wizard.updateStructure({ generatedStructure: { modules: result.modules } });
      } else if (current) {
        const updatedModules = [...current.modules];
        const prev = updatedModules[result.moduleIndex];
        updatedModules[result.moduleIndex] = {
          ...prev,
          ...result.module,
          learningOutcomesServed: result.module.learningOutcomesServed ?? prev?.learningOutcomesServed ?? [],
          ragDocumentTitles: result.module.ragDocumentTitles ?? prev?.ragDocumentTitles ?? [],
          lessons: (result.module.lessons ?? prev?.lessons ?? []).map((l: any) => ({
            title: l.title ?? '',
            reasoning: l.reasoning ?? '',
            // What each lesson is for, where the structure says (the Capability build flow).
            ...(l.capabilityId !== undefined || Array.isArray(l.outcomes)
              ? { capabilityId: l.capabilityId ?? null, outcomes: l.outcomes ?? [], roleplay: l.roleplay === true }
              : {})
          }))
        };
        wizard.updateStructure({ generatedStructure: { modules: updatedModules } });
      }
    } catch (err: any) {
        setGenerationError(failureMessage(err, 'Elliott could not improve this module. Try again.'));
      console.error('[AIMDv2] improveModule failed', err);
    } finally {
      setGenerationPhase('idle');
    }
  }, [wizard, ensureGenerationId]);

  const handleImproveProgram = useCallback(async (feedback: string) => {
    try {
      setGenerationPhase('structure');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      const payload = getCommonPayload(wizard);
      const result = await aimdApi.improveProgram(genId, {
        generalPurpose: payload.generalPurpose,
        learningObjectives: payload.learningObjectives,
        feedback,
        programPreview: payload.programPreview
      });
      wizard.updateStructure({ generatedStructure: result as ProgramStructure });
    } catch (err: any) {
        setGenerationError(failureMessage(err, 'Elliott could not improve the program structure. Try again.'));
      console.error('[AIMDv2] improveProgram failed', err);
    } finally {
      setGenerationPhase('idle');
    }
  }, [wizard, ensureGenerationId]);

  const [structureSavedAt, setStructureSavedAt] = useState(0);
  const handleSaveStructure = useCallback(async (modules?: ModuleStructure[]) => {
    try {
      const genId = await ensureGenerationId();
      const structure = wizard.state.structure.generatedStructure as ProgramStructure | null;
      const sent = modules ?? structure?.modules;
      if (!sent) return;
      const saved = await aimdApi.saveManualStructure(genId, sent);
      setStructureSavedAt(Date.now());
      // The backend keeps each lesson's tags and Scored Roleplay flag (it never moves one once lessons are
      // written, docs/PRD-capability-lesson-roleplays.md D3): take them back onto the lessons still in place.
      const kept: Array<{ title?: string; lessons?: Array<{ title?: string; capabilityId?: string | null; outcomes?: string[]; roleplay?: boolean }> }> =
        Array.isArray(saved?.modules) ? saved.modules : [];
      const current = wizardStateRef.current.structure.generatedStructure as ProgramStructure | null;
      if (current && kept.some((m) => m.lessons?.some((l) => typeof l.roleplay === 'boolean'))) {
        const updated = current.modules.map((m, mi) => ({
            ...m,
            lessons: m.lessons.map((l, li) => {
              const k = kept[mi]?.lessons?.[li];
              return k && k.title === l.title && typeof k.roleplay === 'boolean'
                ? { ...l, capabilityId: k.capabilityId ?? null, outcomes: k.outcomes ?? [], roleplay: k.roleplay }
                : l;
            })
          }));
        wizard.updateStructure({ generatedStructure: { ...current, modules: updated } });
      }
    } catch (err: any) {
      // Refused (the Capability build flow: a structure that leaves one of the program's capabilities
      // with no lesson, 409): the admin's to fix, so the reason goes back to the caller. Anything
      // else is only logged, as before.
      if (err?.response?.status === 409) throw new Error(failureMessage(err, 'This structure could not be saved.'));
      console.error('[AIMDv2] saveStructure failed', err);
    }
  }, [wizard, ensureGenerationId]);

  // ----- Content -----

  const stopContentPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
    setPollingActive(false);
  }, []);

  const startContentPolling = useCallback(() => {
    const genId = wizard.state.generationId;
    if (!genId) return;

    setPollingActive(true);
    setGenerationPhase('content');
    elliottWaitTicksRef.current = 0;

    const poll = async () => {
      try {
        const server: GenerationStatus = await aimdApi.pollGenerationStatus(genId);
        setGenerationProgress(server.progress);

        // Patch-based call — updateContent auto-merges, preserving
        // any locally-completed (potentially edited) lessons.
        wizard.updateContent({ generatedContent: server });

        if (server.status === 'failed') {
          stopContentPolling();
          setGenerationPhase('idle');
          setGenerationError(server.error || 'The lessons could not be built. Try again.');
        } else if (server.status === 'completed') {
          // Content itself is done — unblock the wizard UI right away.
          setGenerationPhase('idle');
          setPollingActive(false);
          // Per-lesson Elliott sections (case studies / simulation / quiz)
          // are generated fire-and-forget on the BE AFTER each lesson's
          // content callback, so they usually land after the status flips
          // to 'completed'. Keep polling quietly until every lesson has
          // them — bounded so a silently-failed cascade can't poll forever.
          const elliottPending = (server.modules || []).some((m) =>
            (m.lessons || []).some((l) => !l.elliott)
          );
          if (!elliottPending || elliottWaitTicksRef.current >= 36) {
            stopContentPolling();
          } else {
            elliottWaitTicksRef.current += 1;
          }
        }
      } catch (err) {
        console.error('[AIMDv2] polling failed', err);
      }
    };

    poll();
    pollingRef.current = setInterval(poll, 5000);
  }, [wizard, stopContentPolling]);

  const handleGenerateContent = useCallback(async () => {
    try {
      setGenerationPhase('content');
      setGenerationError(null);
      const genId = await ensureGenerationId();

      // First save the structure
      await handleSaveStructure();

      const payload = getCommonPayload(wizard);
      const structure = wizard.state.structure.generatedStructure as ProgramStructure | null;
      if (!structure) throw new Error('Add a structure before building the lessons.');

      const modules = structure.modules.map((m) => ({
        title: m.title,
        lessons: m.lessons.map(lessonForGeneration)
      }));

      await aimdApi.generateContent(genId, {
        generalPurpose: payload.generalPurpose,
        learningObjectives: payload.learningObjectives,
        modules,
        orgDocsIds: payload.orgDocsIds,
        programPreview: payload.programPreview
      });

      startContentPolling();
    } catch (err: any) {
      setGenerationError(failureMessage(err, 'The lessons could not be built. Try again.'));
      setGenerationPhase('idle');
      console.error('[AIMDv2] generateContent failed', err);
    }
  }, [wizard, ensureGenerationId, handleSaveStructure, startContentPolling]);

  const handleRegenerateLesson = useCallback(async (
    moduleIndex: number,
    lessonIndex: number,
    feedback: string
  ) => {
    try {
      setGenerationError(null);

      // Immediately set lesson status to 'generating' so the UI shows the spinner
      wizard.updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules?.[moduleIndex]?.lessons?.[lessonIndex]) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== moduleIndex) return m;
          const lessons = m.lessons.map((l, li) =>
            li === lessonIndex ? { ...l, status: 'generating' as const } : l
          );
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });

      const genId = await ensureGenerationId();
      const result = await aimdApi.regenerateLessonContent(genId, moduleIndex, lessonIndex, feedback);

      // Use functional updater to always read the latest state
      wizard.updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules?.[moduleIndex]) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== moduleIndex) return m;
          const lessons = m.lessons.map((l, li) => {
            if (li !== lessonIndex) return l;
            const merged = { ...l, ...result.lesson, status: 'completed' as const };
            // An AI-generated video the admin added before improving lives in
            // the old lesson HTML; the regenerated prose doesn't include it.
            // Carry it over instead of silently deleting their video.
            return { ...merged, content: preserveVideos(l.content, merged.content || '') };
          });
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
    } catch (err: any) {
        setGenerationError(err?.message || 'Elliott could not rewrite this lesson. Try again.');
      console.error('[AIMDv2] regenerateLesson failed', err);
      // Revert to completed on error
      wizard.updateContent((prev: ContentData): ContentData => {
        const gen = prev.generatedContent as GenerationStatus | null;
        if (!gen?.modules?.[moduleIndex]?.lessons?.[lessonIndex]) return prev;
        const modules = gen.modules.map((m, mi) => {
          if (mi !== moduleIndex) return m;
          const lessons = m.lessons.map((l, li) =>
            li === lessonIndex ? { ...l, status: 'completed' as const } : l
          );
          return { ...m, lessons };
        });
        return { ...prev, generatedContent: { ...gen, modules } };
      });
    }
  }, [wizard, ensureGenerationId]);

  const handleRegenerateModule = useCallback(async (moduleIndex: number, feedback: string) => {
    try {
      setGenerationPhase('content');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      await aimdApi.regenerateModuleContent(genId, moduleIndex, feedback);
      startContentPolling();
    } catch (err: any) {
        setGenerationError(err?.message || 'Elliott could not rewrite this module. Try again.');
      setGenerationPhase('idle');
      console.error('[AIMDv2] regenerateModule failed', err);
    }
  }, [ensureGenerationId, startContentPolling]);

  const handleRegenerateProgram = useCallback(async (feedback: string) => {
    try {
      setGenerationPhase('content');
      setGenerationError(null);
      const genId = await ensureGenerationId();
      // Send the admin's CURRENT (edited) structure so the BE regenerates
      // content for that shape instead of re-deriving the original default
      // 4×4 program. Read from the ref so we don't capture a stale closure.
      const content = wizardStateRef.current.content.generatedContent as GenerationStatus | null;
      const modules = content?.modules?.map((m) => ({
        title: m.title,
        lessons: m.lessons.map(lessonForGeneration)
      }));
      await aimdApi.regenerateProgramContent(genId, feedback, modules);
      startContentPolling();
    } catch (err: any) {
        setGenerationError(failureMessage(err, 'Elliott could not rewrite the program. Try again.'));
      setGenerationPhase('idle');
      console.error('[AIMDv2] regenerateProgram failed', err);
    }
  }, [ensureGenerationId, startContentPolling]);

  const handlePublish = useCallback(async () => {
    const genId = await ensureGenerationId();
    // The artifact and the browser must agree before Publish: the BE copies
    // every lesson saved through the content endpoint onto the template, so
    // an edit still only in the browser would be overwritten by the older
    // saved copy. A failed flush aborts the publish for a retry.
    const flushed = await handleSaveContent();
    if (!flushed.ok) {
      throw new Error(
        flushed.message || 'Your latest lesson edits could not be saved. Please try again before publishing.'
      );
    }
    const latestState = wizardStateRef.current;
    const content = latestState.content.generatedContent as GenerationStatus | null;
    if (!content?.modules) throw new Error('No content to publish');

    const goalPayload = aimdApi.buildGoalPayload(
      latestState.requirements.programName,
      content.modules.map((m) => ({
        title: m.title,
        reasoning: m.reasoning,
        lessons: m.lessons.map((l) => ({
          title: l.title,
          content: l.content,
          mcq: l.mcq,
          mcqs: l.mcqs,
          materials: l.materials
        }))
      }))
    );
    await aimdApi.goalUpload(wizard.groupId, goalPayload);

    const { orgId, location } = getProgramContext();

    await Promise.all([
      API.updateTopicsAndIndustry({
        grpId: wizard.groupId,
        orgId,
        data: { topics: ['Topic Creation'] },
        key: 'topics'
      }),
      API.updateTopicsAndIndustry({
        grpId: wizard.groupId,
        orgId,
        data: { industries: ['Industry Creation'] },
        key: 'industries'
      })
    ]);

    const basicSettingsForm = new FormData();
    basicSettingsForm.append('name', latestState.requirements.programName);
    basicSettingsForm.append('model', 'masterclass');
    basicSettingsForm.append('mentee', 'mentee');
    basicSettingsForm.append('mentor', 'mentor');
    await API.updateGroupInfo({
      orgId,
      grpId: wizard.groupId,
      formData: basicSettingsForm
    });

    await API.updateGroupSettings({ groupId: wizard.groupId, location });

    await aimdApi.publishProgramGeneration(wizard.groupId, genId);

    dispatch(fetchGroupDetails({ grpId: wizard.groupId }) as any);
  }, [wizard.groupId, ensureGenerationId, handleSaveContent, dispatch]);

  // Wrap goNext so that leaving the requirements step eagerly creates the
  // generation + group.  This ensures groupId is available by the time the
  // Documents step mounts (Documents needs groupId to fetch/upload docs).
  const wrappedGoNext = useCallback((): boolean => {
    const leavingStep = AIMD_STEPS[wizard.state.currentStep];
    const result = wizard.goNext();
    if (result && leavingStep === 'requirements') {
      ensureGenerationId().catch((err) =>
        console.error('[AIMDv2] Early ensureGenerationId failed', err)
      );
    }
    return result;
  }, [wizard, ensureGenerationId]);

  const value: AIMDContextValue = {
    ...wizard,
    updateContent,
    goNext: wrappedGoNext,
    generationPhase,
    isGenerating,
    pollingActive,
    generationProgress,
    generationError,
    handleGeneratePreview,
    handleImprovePreview,
    handleGenerateStructure,
    handleImproveModule,
    handleImproveProgram,
    handleSaveStructure,
    structureSavedAt,
    handleSaveContent,
    markAllLessonsClean,
    reloadGenerationFromServer,
    handleGenerateContent,
    handleRegenerateLesson,
    handleRegenerateModule,
    handleRegenerateProgram,
    handlePublish,
    startContentPolling,
    stopContentPolling,
    embedded
  };

  return <AIMDContext.Provider value={value}>{children}</AIMDContext.Provider>;
};

// ---------------------------------------------------------------------------
// Consumer hook
// ---------------------------------------------------------------------------

export function useAIMDContext(): AIMDContextValue {
  const ctx = useContext(AIMDContext);
  if (!ctx) {
    throw new Error('useAIMDContext must be used within an AIMDProvider');
  }
  return ctx;
}
